// Predefined options for skills, languages, and certifications

export const CAREGIVER_SKILLS = [
  { id: 'personal_care', label: 'Personal Care (Bathing, Grooming)' },
  { id: 'meal_prep', label: 'Meal Preparation' },
  { id: 'medication_reminders', label: 'Medication Reminders' },
  { id: 'medication_administration', label: 'Medication Administration' },
  { id: 'mobility_assistance', label: 'Mobility Assistance' },
  { id: 'transfer_assistance', label: 'Transfer Assistance' },
  { id: 'light_housekeeping', label: 'Light Housekeeping' },
  { id: 'laundry', label: 'Laundry' },
  { id: 'companionship', label: 'Companionship' },
  { id: 'transportation', label: 'Transportation' },
  { id: 'errands', label: 'Errands & Shopping' },
  { id: 'feeding_assistance', label: 'Feeding Assistance' },
  { id: 'incontinence_care', label: 'Incontinence Care' },
  { id: 'wound_care', label: 'Basic Wound Care' },
  { id: 'advanced_wound_care', label: 'Advanced Wound Care' },
  { id: 'vital_signs', label: 'Vital Signs Monitoring' },
  { id: 'iv_therapy', label: 'IV Therapy' },
  { id: 'catheter_care', label: 'Catheter Care' },
  { id: 'tracheostomy_care', label: 'Tracheostomy Care' },
  { id: 'ventilator_care', label: 'Ventilator Care' },
  { id: 'g_tube_feeding', label: 'G-Tube Feeding' },
  { id: 'insulin_administration', label: 'Insulin Administration' },
  { id: 'blood_glucose_monitoring', label: 'Blood Glucose Monitoring' },
  { id: 'patient_assessment', label: 'Patient Assessment' },
  { id: 'care_plan_development', label: 'Care Plan Development' },
  { id: 'dementia_care', label: 'Dementia/Alzheimer\'s Care' },
  { id: 'hospice_support', label: 'Hospice Support' },
  { id: 'pediatric_care', label: 'Pediatric Care' },
  { id: 'respite_care', label: 'Respite Care' },
  { id: 'live_in', label: 'Live-In Care' },
  { id: 'overnight', label: 'Overnight Care' },
] as const

export const LANGUAGES = [
  { id: 'english', label: 'English' },
  { id: 'spanish', label: 'Spanish' },
  { id: 'mandarin', label: 'Mandarin Chinese' },
  { id: 'cantonese', label: 'Cantonese' },
  { id: 'tagalog', label: 'Tagalog' },
  { id: 'vietnamese', label: 'Vietnamese' },
  { id: 'korean', label: 'Korean' },
  { id: 'french', label: 'French' },
  { id: 'german', label: 'German' },
  { id: 'arabic', label: 'Arabic' },
  { id: 'russian', label: 'Russian' },
  { id: 'portuguese', label: 'Portuguese' },
  { id: 'italian', label: 'Italian' },
  { id: 'hindi', label: 'Hindi' },
  { id: 'urdu', label: 'Urdu' },
  { id: 'polish', label: 'Polish' },
  { id: 'japanese', label: 'Japanese' },
  { id: 'haitian_creole', label: 'Haitian Creole' },
  { id: 'asl', label: 'American Sign Language' },
  { id: 'other', label: 'Other' },
] as const

export const CERTIFICATIONS = [
  { id: 'rn', label: 'RN (Registered Nurse)' },
  { id: 'lpn', label: 'LPN (Licensed Practical Nurse)' },
  { id: 'cna', label: 'CNA (Certified Nursing Assistant)' },
  { id: 'hha', label: 'HHA (Home Health Aide)' },
  { id: 'chha', label: 'CHHA (Certified Home Health Aide)' },
  { id: 'pca', label: 'PCA (Personal Care Aide)' },
  { id: 'dsp', label: 'DSP (Direct Support Professional)' },
  { id: 'med_tech', label: 'Med Tech (Medication Technician)' },
  { id: 'cpr', label: 'CPR Certified' },
  { id: 'first_aid', label: 'First Aid Certified' },
  { id: 'bls', label: 'BLS (Basic Life Support)' },
  { id: 'acls', label: 'ACLS (Advanced Cardiac Life Support)' },
  { id: 'dementia_certified', label: 'Dementia Care Certified' },
  { id: 'alzheimers_certified', label: 'Alzheimer\'s Care Certified' },
  { id: 'hospice_certified', label: 'Hospice Care Certified' },
  { id: 'pediatric_certified', label: 'Pediatric Care Certified' },
  { id: 'medication_admin', label: 'Medication Administration' },
  { id: 'tb_test', label: 'TB Test (Current)' },
  { id: 'background_check', label: 'Background Check Cleared' },
  { id: 'drivers_license', label: 'Valid Driver\'s License' },
  { id: 'auto_insurance', label: 'Auto Insurance' },
] as const

// Helper functions
export function getSkillLabel(id: string): string {
  return CAREGIVER_SKILLS.find(s => s.id === id)?.label || id
}

export function getLanguageLabel(id: string): string {
  return LANGUAGES.find(l => l.id === id)?.label || id
}

export function getCertificationLabel(id: string): string {
  return CERTIFICATIONS.find(c => c.id === id)?.label || id
}

// Check if caregiver matches client requirements
export function checkCaregiverClientMatch(
  caregiver: {
    skills?: string[]
    languages?: string[]
    certifications?: string[]
  },
  client: {
    requiredSkills?: string[]
    preferredLanguages?: string[]
    requiredCertifications?: string[]
  }
): {
  isMatch: boolean
  matchScore: number
  missingSkills: string[]
  missingLanguages: string[]
  missingCertifications: string[]
} {
  const missingSkills = (client.requiredSkills || []).filter(
    skill => !(caregiver.skills || []).includes(skill)
  )
  const missingLanguages = (client.preferredLanguages || []).filter(
    lang => !(caregiver.languages || []).includes(lang)
  )
  const missingCertifications = (client.requiredCertifications || []).filter(
    cert => !(caregiver.certifications || []).includes(cert)
  )

  // Calculate match score (0-100)
  const totalRequirements =
    (client.requiredSkills?.length || 0) +
    (client.preferredLanguages?.length || 0) +
    (client.requiredCertifications?.length || 0)

  const missingCount =
    missingSkills.length + missingLanguages.length + missingCertifications.length

  const matchScore = totalRequirements > 0
    ? Math.round(((totalRequirements - missingCount) / totalRequirements) * 100)
    : 100

  return {
    isMatch: missingSkills.length === 0 && missingCertifications.length === 0,
    matchScore,
    missingSkills,
    missingLanguages,
    missingCertifications,
  }
}

export const CAREGIVER_POSITIONS = [
  { id: 'RN', label: 'RN (Registered Nurse)' },
  { id: 'LPN', label: 'LPN (Licensed Practical Nurse)' },
  { id: 'CNA', label: 'CNA (Certified Nursing Assistant)' },
  { id: 'MED_TECH', label: 'Med Tech (Medication Technician)' },
  { id: 'HHA', label: 'HHA (Home Health Aide)' },
  { id: 'CHHA', label: 'CHHA (Certified Home Health Aide)' },
  { id: 'PCA', label: 'PCA (Personal Care Aide)' },
  { id: 'DSP', label: 'DSP (Direct Support Professional)' },
  { id: 'OTHER', label: 'Other' },
] as const

export const CLIENT_CASE_TYPES = [
  {
    value: 'cna_hha_pca',
    label: 'CNA/HHA/PCA Case',
    description: 'Personal care or aide-level case. RN/LPN caregivers are not eligible for pickup.',
  },
  {
    value: 'rn_lpn',
    label: 'RN/LPN Case',
    description: 'Nursing case. CNA/HHA/PCA caregivers are not eligible for pickup.',
  },
] as const

export type ClientCaseTypeOption = typeof CLIENT_CASE_TYPES[number]['value']

const CLIENT_CASE_TYPE_LABELS: Record<string, string> = {
  cna_hha_pca: 'CNA/HHA/PCA Case',
  rn_lpn: 'RN/LPN Case',
}

const CNA_HHA_PCA_POSITIONS = new Set(['CNA', 'HHA', 'CHHA', 'PCA'])
const RN_LPN_POSITIONS = new Set(['RN', 'LPN'])

export function normalizeDisciplineIdentifier(value?: string | null): string {
  const normalized = (value || '').trim().toUpperCase()
  if (!normalized) return ''

  if (normalized === 'RN' || normalized.includes('REGISTERED NURSE')) return 'RN'
  if (normalized === 'LPN' || normalized.includes('LICENSED PRACTICAL NURSE')) return 'LPN'
  if (normalized === 'CNA' || normalized.includes('CERTIFIED NURSING ASSISTANT')) return 'CNA'
  if (normalized === 'HHA' || normalized.includes('HOME HEALTH AIDE')) return 'HHA'
  if (normalized === 'CHHA' || normalized.includes('CERTIFIED HOME HEALTH AIDE')) return 'CHHA'
  if (normalized === 'PCA' || normalized.includes('PERSONAL CARE AIDE')) return 'PCA'

  return CAREGIVER_POSITION_BADGES[value || ''] || normalized
}

export function getClientCaseTypeLabel(caseType?: string | null): string {
  return caseType ? CLIENT_CASE_TYPE_LABELS[caseType] || caseType : ''
}

export function inferClientCaseType(client?: {
  caseType?: string | null
  requiredCertifications?: string[]
} | null): ClientCaseTypeOption | undefined {
  if (client?.caseType === 'cna_hha_pca' || client?.caseType === 'rn_lpn') {
    return client.caseType
  }

  const certifications = (client?.requiredCertifications || []).map(normalizeDisciplineIdentifier)
  if (certifications.some(cert => RN_LPN_POSITIONS.has(cert))) return 'rn_lpn'
  if (certifications.some(cert => CNA_HHA_PCA_POSITIONS.has(cert))) return 'cna_hha_pca'

  return undefined
}

export function getCaregiverDisciplineGroup(caregiver?: {
  position?: string | null
  certifications?: string[]
} | null): ClientCaseTypeOption | undefined {
  if (caregiver?.position) {
    const position = normalizeDisciplineIdentifier(caregiver.position)
    if (RN_LPN_POSITIONS.has(position)) return 'rn_lpn'
    if (CNA_HHA_PCA_POSITIONS.has(position)) return 'cna_hha_pca'
  }

  const certifications = (caregiver?.certifications || [])
    .filter((value): value is string => Boolean(value))
    .map(normalizeDisciplineIdentifier)

  if (certifications.some(certification => RN_LPN_POSITIONS.has(certification))) return 'rn_lpn'
  if (certifications.some(certification => CNA_HHA_PCA_POSITIONS.has(certification))) return 'cna_hha_pca'

  return undefined
}

export function caregiverMatchesClientCaseType(
  caregiver: {
    position?: string | null
    certifications?: string[]
  } | null | undefined,
  client: {
    caseType?: string | null
    requiredCertifications?: string[]
  } | null | undefined
): boolean {
  const clientCaseType = inferClientCaseType(client)
  if (!clientCaseType) return true

  return getCaregiverDisciplineGroup(caregiver) === clientCaseType
}

const CAREGIVER_POSITION_LABELS: Record<string, string> = {
  RN: 'RN (Registered Nurse)',
  LPN: 'LPN (Licensed Practical Nurse)',
  CNA: 'CNA (Certified Nursing Assistant)',
  MED_TECH: 'Med Tech (Medication Technician)',
  HHA: 'HHA (Home Health Aide)',
  CHHA: 'CHHA (Certified Home Health Aide)',
  PCA: 'PCA (Personal Care Aide)',
  DSP: 'DSP (Direct Support Professional)',
  OTHER: 'Other',
  rn: 'RN (Registered Nurse)',
  lpn: 'LPN (Licensed Practical Nurse)',
  cna: 'CNA (Certified Nursing Assistant)',
  med_tech: 'Med Tech (Medication Technician)',
  hha: 'HHA (Home Health Aide)',
  chha: 'CHHA (Certified Home Health Aide)',
  pca: 'PCA (Personal Care Aide)',
  dsp: 'DSP (Direct Support Professional)',
}

const CAREGIVER_POSITION_BADGES: Record<string, string> = {
  RN: 'RN',
  LPN: 'LPN',
  CNA: 'CNA',
  MED_TECH: 'Med Tech',
  HHA: 'HHA',
  CHHA: 'CHHA',
  PCA: 'PCA',
  DSP: 'DSP',
  OTHER: 'Other',
  rn: 'RN',
  lpn: 'LPN',
  cna: 'CNA',
  med_tech: 'Med Tech',
  hha: 'HHA',
  chha: 'CHHA',
  pca: 'PCA',
  dsp: 'DSP',
}

const CERTIFICATION_TO_POSITION: Record<string, string> = {
  rn: 'RN',
  lpn: 'LPN',
  cna: 'CNA',
  med_tech: 'MED_TECH',
  hha: 'HHA',
  chha: 'CHHA',
  pca: 'PCA',
  dsp: 'DSP',
}

export function inferCaregiverPositionFromCertifications(certifications: string[] = []): string | undefined {
  for (const certification of certifications) {
    const inferred = CERTIFICATION_TO_POSITION[certification]
    if (inferred) return inferred
  }
  return undefined
}

export function getCaregiverPositionLabel(position?: string | null, customPosition?: string | null): string {
  if (position === 'OTHER' && customPosition?.trim()) {
    return customPosition.trim()
  }

  if (position && CAREGIVER_POSITION_LABELS[position]) {
    return CAREGIVER_POSITION_LABELS[position]
  }

  if (customPosition?.trim()) {
    return customPosition.trim()
  }

  return position || ''
}

export function getCaregiverPositionBadge(position?: string | null, customPosition?: string | null): string {
  if (position === 'OTHER' && customPosition?.trim()) {
    return customPosition.trim()
  }

  if (position && CAREGIVER_POSITION_BADGES[position]) {
    return CAREGIVER_POSITION_BADGES[position]
  }

  if (customPosition?.trim()) {
    return customPosition.trim()
  }

  return position || ''
}
